import { useQueryClient } from '@tanstack/react-query'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import axios from 'axios'
import { deleteChargingCardApiErrors } from './dto/charging-cards-api-errors'
import { ApiResult, usePortalMutation } from 'src/Libs/api'

type UseDeleteChargingCardHookProps = {
  installationId: string
  ocpiTokenId: string
}

interface UseDeleteChargingCardHook {
  isLoading: boolean
  deleteChargingCard: ({ installationId, ocpiTokenId }: UseDeleteChargingCardHookProps) => void
}

/**
 * Function for delete charging card data
 * @param ocpiTokenId - - unique charging card identifier as a string
 * @param onDeleteSuccess - function for closing dialog on success method
 * @param installationId - unique installation identifier as a string
 * @returns isLoading as boolean
 * @returns mutate function from react-query
 */
export const useDeleteChargingCard = (onDeleteSuccess: () => void, installationId: string): UseDeleteChargingCardHook => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResult()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const queryClient = useQueryClient()

  const deleteChargingCardApi = async ({ installationId, ocpiTokenId }: UseDeleteChargingCardHookProps): Promise<ApiResult<string>> => {
    const chargingCardResponse = authFetch<string>({
      path: `tokens/installation/${Number(installationId)}/ocpiTokenId/${ocpiTokenId}`,
      endpoint: process.env.REACT_APP_HAGER_BRIDGE_SERVICE,
      method: 'DELETE',
    })

    return new ApiResult(await chargingCardResponse)
  }

  const { isPending, mutate: deleteChargingCard } = usePortalMutation({
    mutationFn: deleteChargingCardApi,
    onError: error => {
      if (axios.isAxiosError(error)) {
        enqueueErrorStack(t(deleteChargingCardApiErrors[error.response?.data.errorCode]))
      } else {
        enqueueErrorStack(t('emobility.chargingCards.delete-charging-card-failed'))
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['chargingCards'] })
      await queryClient.invalidateQueries({ queryKey: ['wallboxes', Number(installationId)] })
      enqueueSuccessStack(t('emobility.chargingCards.delete-charging-card-success'))
      onDeleteSuccess()
    },
  })

  return { isLoading: isPending, deleteChargingCard }
}
